import { FC, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Expanded, ExpandedBlock, FramerTooltip } from "@ryde/controls";
import {
  getProcessBulkCounts,
  PLURAL_KEY,
  PROCESS_SUCCESS_IDS_DEFAULT_COLLAPSED_FROM,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import { ProcessDrawerContext } from "../process-drawer-context";
import {
  PROCESS_WARNING_ACCENT,
  ProcessIdsSectionStyled,
  ProcessIdsTitleStyled,
  ProcessSplitBarSegmentStyled,
  ProcessSplitBarStyled,
  ProcessStatCardStyled,
  ProcessStatLabelStyled,
  ProcessStatsGridStyled,
  ProcessStatValueStyled,
  ProcessSummaryBlockStyled,
  ProcessSummaryCaptionStyled,
} from "../process.styled";
import { ProcessEntityIdChips } from "./process-entity-id-chips";

export const ProcessStatisticGeneric: FC = () => {
  const { t } = useTranslation();
  const { currentProcess, entity } = useContext(ProcessDrawerContext);
  const { totalCount, successCount, skippedCount, warningCount } =
    getProcessBulkCounts(currentProcess ?? {});
  const successIds = currentProcess?.metadata?.metrics?.successIds ?? [];
  const successIdsLabel = entity
    ? t("process.successfulIdsLabel", {
        entity: t(`${entity}.${PLURAL_KEY}`),
        count: successIds.length,
      })
    : undefined;
  const summaryCaption = useMemo(() => {
    if (skippedCount === 0 && warningCount === 0) {
      return t("process.bulkSummaryAllSuccess", {
        successCount,
        totalCount,
      });
    }
    if (skippedCount === 0) {
      return t("process.bulkSummaryAllSuccessWithWarnings", {
        successCount,
        totalCount,
        warningCount,
      });
    }
    if (successCount === 0) {
      return t("process.bulkSummaryAllSkipped", {
        skippedCount,
        totalCount,
      });
    }
    return t(
      warningCount
        ? "process.bulkSummaryPartialWithWarnings"
        : "process.bulkSummaryPartial",
      {
        successCount,
        skippedCount,
        totalCount,
        warningCount,
      },
    );
  }, [skippedCount, successCount, t, totalCount, warningCount]);
  const statsColumns = warningCount > 0 ? 4 : 3;

  return (
    <ProcessSummaryBlockStyled>
      <ProcessStatsGridStyled $columns={statsColumns}>
        <ProcessStatCardStyled $accent={rydeColors.grey}>
          <ProcessStatLabelStyled>
            {t("process.genericTotalTitle")}
          </ProcessStatLabelStyled>
          <ProcessStatValueStyled $color={rydeColors.placeholder}>
            {totalCount}
          </ProcessStatValueStyled>
        </ProcessStatCardStyled>
        <ProcessStatCardStyled $accent={rydeColors.green}>
          <ProcessStatLabelStyled>
            {t("process.genericSuccessfulTitle")}
          </ProcessStatLabelStyled>
          <ProcessStatValueStyled $color={rydeColors.green}>
            {successCount}
          </ProcessStatValueStyled>
        </ProcessStatCardStyled>
        <ProcessStatCardStyled $accent={rydeColors.orange}>
          <ProcessStatLabelStyled>
            {t("process.genericSkippedTitle")}
          </ProcessStatLabelStyled>
          <ProcessStatValueStyled $color={rydeColors.orange}>
            {skippedCount}
          </ProcessStatValueStyled>
        </ProcessStatCardStyled>
        {warningCount > 0 ? (
          <FramerTooltip content={t("process.warnings")}>
            <ProcessStatCardStyled $accent={PROCESS_WARNING_ACCENT}>
              <ProcessStatLabelStyled>
                {t("process.genericWarningsTitle")}
              </ProcessStatLabelStyled>
              <ProcessStatValueStyled $color={PROCESS_WARNING_ACCENT}>
                {warningCount}
              </ProcessStatValueStyled>
            </ProcessStatCardStyled>
          </FramerTooltip>
        ) : null}
      </ProcessStatsGridStyled>

      <ProcessSplitBarStyled>
        <ProcessSplitBarSegmentStyled
          $flex={successCount || 0}
          $color={rydeColors.green}
        />
        <ProcessSplitBarSegmentStyled
          $flex={skippedCount || 0}
          $color={rydeColors.orange}
        />
        <ProcessSplitBarSegmentStyled
          $flex={Math.max(totalCount - successCount - skippedCount, 0)}
          $color={rydeColors.lightGrey}
        />
      </ProcessSplitBarStyled>
      <ProcessSummaryCaptionStyled>
        {summaryCaption}
      </ProcessSummaryCaptionStyled>

      {successIds.length && entity && successIdsLabel ? (
        <ProcessIdsSectionStyled>
          <Expanded
            defaultExpanded={
              successIds.length <= PROCESS_SUCCESS_IDS_DEFAULT_COLLAPSED_FROM
            }
            NavControl={
              <ProcessIdsTitleStyled>{successIdsLabel}</ProcessIdsTitleStyled>
            }
          >
            <ExpandedBlock blockHeight="auto">
              <ProcessEntityIdChips
                entity={entity}
                ids={successIds}
                tone="success"
              />
            </ExpandedBlock>
          </Expanded>
        </ProcessIdsSectionStyled>
      ) : null}
    </ProcessSummaryBlockStyled>
  );
};
